import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';

import UserContext from '../../../context/UserContext';
import Track from '../../../Model/Track';
import useSessionStore from '../../../hooks/useSessionStore';
import useComponentMounted from '../../../hooks/useComponentMounted';
import PrimaryContextsReadyContext from '../PrimaryContextsReadyContext';
import ExternalCoachContext from '../ExternalCoachContext/ExternalCoachContext';
import ManageTrackContext, { initialValues } from './ManageTrackContext';

const ManageTrackContextProvider = ({
  children,
}) => {
  const [coachTracksCollection, setCoachTracksCollection] = useState(initialValues.coachTracksCollection);
  const [publicTracksCollection, setPublicTracksCollection] = useState(initialValues.publicTracksCollection);
  const [coachTracks, setCoachTracks] = useState(initialValues.coachTracks);
  const [showArchivedCoachTracks, setShowArchivedCoachTracks] = useState(initialValues.showArchivedCoachTracks);
  const [equipmentList, setEquipmentList] = useState(initialValues.equipmentList);
  const [isReady, setIsReady] = useState(initialValues.isReady);

  const { isCoachAssistant, isAdmin } = useSessionStore();
  const isComponentMountedRef = useComponentMounted();

  const { readyForDeferredContexts } = useContext(PrimaryContextsReadyContext);
  const { userId: coachId } = useContext(UserContext);
  const { coachDoc } = useContext(ExternalCoachContext);

  const isPublicTracksCoach = !!coachDoc?.isPublicTracksCoach;

  const getTrackCollections = useCallback(async (fetchArchivedTracks = false) => {
    // If the current coach is the Public Workouts coach, skip fetching coach-specific tracks
    // since they're already included in public tracks (avoiding duplication)
    const coachCollection = (coachId && !isPublicTracksCoach)
      ? await Track.getTracksByCoach(coachId, fetchArchivedTracks)
      : initialValues.coachTracksCollection;
    const publicCollection = (isCoachAssistant || isAdmin)
      ? await Track.getPublicTracks(fetchArchivedTracks)
      : initialValues.publicTracksCollection;
    return {
      coachCollection,
      publicCollection,
    };
  }, [
    coachId,
    isCoachAssistant,
    isAdmin,
    isPublicTracksCoach,
  ]);

  useEffect(() => {
    const init = async () => {
      if (!isCoachAssistant && !isAdmin) {
        if (isComponentMountedRef.current) {
          setIsReady(true);
        }
        return;
      }
      const {
        coachCollection,
        publicCollection,
      } = await getTrackCollections(false);
      if (isComponentMountedRef.current) {
        setCoachTracksCollection(coachCollection);
        setPublicTracksCollection(publicCollection);
        setIsReady(true);
      }
    };

    if (coachId && readyForDeferredContexts) {
      init();
    }
  }, [
    coachId,
    readyForDeferredContexts,
    isComponentMountedRef,
    getTrackCollections,
    isCoachAssistant,
    isAdmin,
  ]);

  useEffect(() => {
    const disposer = autorun(() => {
      const coachTracksDocs = coachTracksCollection.hasDocs ? coachTracksCollection.docs.slice() : [];
      const publicTracksDocs = publicTracksCollection.hasDocs ? publicTracksCollection.docs.slice() : [];
      const uniqueEquipmentMap = new Map();
      if (coachTracksCollection.hasDocs || publicTracksCollection.hasDocs) {
        [...coachTracksDocs, ...publicTracksDocs].forEach((track) => {
          if (track.equipment && track.equipment.length > 0) {
            track.equipment.forEach((equipment) => {
              if (!uniqueEquipmentMap.has(equipment)) {
                uniqueEquipmentMap.set(equipment, {
                  value: equipment,
                  label: equipment,
                });
              }
            });
          }
        });
      }
      const uniqueEquipmentList = Array.from(uniqueEquipmentMap.values());
      setEquipmentList(uniqueEquipmentList);
      setCoachTracks([...coachTracksDocs, ...publicTracksDocs]);
    });
    return disposer;
  }, [
    coachTracksCollection,
    isComponentMountedRef,
    publicTracksCollection,
  ]);

  const refetchCoachTracks = useCallback(async (fetchArchivedTracks = false) => {
    setShowArchivedCoachTracks(fetchArchivedTracks);
    const {
      coachCollection,
      publicCollection,
    } = await getTrackCollections(fetchArchivedTracks);
    if (isComponentMountedRef.current) {
      setCoachTracksCollection(coachCollection);
      setPublicTracksCollection(publicCollection);
    }
  }, [
    getTrackCollections,
    isComponentMountedRef,
  ]);

  const value = useMemo(() => ({
    coachTracksCollection,
    publicTracksCollection,
    isReady,
    coachTracks,
    showArchivedCoachTracks,
    refetchCoachTracks,
    equipmentList,
  }), [
    coachTracksCollection,
    publicTracksCollection,
    isReady,
    coachTracks,
    showArchivedCoachTracks,
    refetchCoachTracks,
    equipmentList,
  ]);

  return (
    <ManageTrackContext.Provider value={value}>
      {children}
    </ManageTrackContext.Provider>
  );
};

ManageTrackContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(ManageTrackContextProvider);
